import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invokeEdgeFunction } from "@/utils/invokeEdgeFunction";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, FileText, Copy } from "lucide-react";
import { CANDIDATE_STATUS, EVALUATION_ROUNDS } from "@/types/enums";
import { formatCandidateStatus } from "@/utils/formatters";
import { getSafeErrorMessage } from "@/utils/safeErrorMessage";
import type { Tables } from "@/integrations/supabase/types";

type Candidate = Tables<"candidates">;

interface CandidateAssessmentActionsProps {
  candidate: Candidate;
  onUpdate?: () => void; // Add onUpdate prop
}

export default function CandidateAssessmentActions({
  candidate,
  onUpdate,
}: CandidateAssessmentActionsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  const isAssessmentRound =
    candidate.current_round === EVALUATION_ROUNDS.CV_PROFILE ||
    candidate.current_round === EVALUATION_ROUNDS.MCQ ||
    candidate.current_round === EVALUATION_ROUNDS.OPEN_ENDED;

  const hasAssessmentState =
    candidate.status === CANDIDATE_STATUS.ASSESSMENT_SENT ||
    candidate.status === CANDIDATE_STATUS.ASSESSMENT_IN_PROGRESS ||
    candidate.status === CANDIDATE_STATUS.ASSESSMENT_COMPLETED ||
    Boolean(candidate.access_token);

  const canSendAssessment =
    candidate.current_round === EVALUATION_ROUNDS.CV_PROFILE ||
    hasAssessmentState;
  // const canSendAssessment = (candidate.current_round === EVALUATION_ROUNDS.CV_PROFILE && candidate.status !=  CANDIDATE_STATUS.FAILED ) || (  CANDIDATE_STATUS.ASSESSMENT_SENT==candidate.status );

  const generateTokenMutation = useMutation({
    mutationFn: async () => {
      const token = crypto.randomUUID();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7); // Token expires in 7 days

      const { data, error } = await supabase
        .from("candidates")
        .update({
          access_token: token,
          token_expires_at: expiresAt.toISOString(),
          status: CANDIDATE_STATUS.ASSESSMENT_SENT,
        })
        .eq("id", candidate.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["candidates"] });
      toast.success("Assessment link generated successfully!");
      onUpdate?.(); // Call onUpdate to refresh candidate data in parent
    },
    onError: (error) => {
      console.error("Error generating token:", error);
      toast.error(
        getSafeErrorMessage(error, "Failed to generate assessment link")
      );
    },
  });

  const sendEmailMutation = useMutation({
    mutationFn: async () => {
      if (!candidate.access_token) {
        throw new Error("No assessment token found");
      }

      await invokeEdgeFunction(
        "send-assessment-email",
        {
          body: {
            candidateId: candidate.id,
            token: candidate.access_token,
          },
        }
      );
    },
    onSuccess: () => {
      toast.success("Assessment email sent successfully!");
      setIsOpen(false);
      onUpdate?.();
    },
    onError: (error) => {
      console.error("Error sending email:", error);
      toast.error(
        getSafeErrorMessage(
          (error as { responseBody?: unknown }).responseBody ?? error,
          "Failed to send assessment email"
        )
      );
    },
  });

  const resumeAndResendMutation = useMutation({
    mutationFn: async () => {
      const result = await invokeEdgeFunction<{
        token: string;
      }>("reset-candidate-assessment", {
        body: {
          candidateId: candidate.id,
          preserveProgress: true,
        },
      });

      await invokeEdgeFunction("send-assessment-email", {
        body: {
          candidateId: candidate.id,
          token: result.token,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["candidates"] });
      toast.success("Resume link emailed successfully!");
      setIsOpen(false);
      onUpdate?.();
    },
    onError: (error) => {
      console.error("Error resuming and resending assessment:", error);
      toast.error(
        getSafeErrorMessage(
          (error as { responseBody?: unknown }).responseBody ?? error,
          "Failed to resend the assessment link"
        )
      );
    },
  });

  const assessmentUrl = candidate.access_token
    ? `${window.location.origin}/assessment?token=${candidate.access_token}`
    : "";

  const copyToClipboard = async () => {
    if (assessmentUrl) {
      await navigator.clipboard.writeText(assessmentUrl);
      toast.success("Assessment link copied to clipboard!");
    }
  };

  const hasValidToken =
    candidate.access_token &&
    candidate.token_expires_at &&
    new Date(candidate.token_expires_at) > new Date();

  const hasExistingAssessmentData = Boolean(
    candidate.assesment_started_at ||
      candidate.assesment_submitted_at ||
      candidate.assessment_completed_at ||
      (candidate.current_group_index ?? 0) > 0
  );

  if (!isAssessmentRound && !hasAssessmentState) return null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          disabled={!canSendAssessment}
          title={
            !canSendAssessment
              ? "Candidate is not eligible for assessment"
              : "Assessment actions"
          }
        >
          <FileText className="h-4 w-4" />
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Assessment Link - {candidate.full_name}</DialogTitle>
          <DialogDescription>
            Generate and send assessment link to the candidate
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!hasValidToken && (
            <div className="py-4 text-center space-y-3">
              <p className="text-sm text-gray-600 mb-4">
                No valid assessment link exists for this candidate.
              </p>
              <Button
                onClick={() => generateTokenMutation.mutate()}
                disabled={
                  generateTokenMutation.isPending ||
                  resumeAndResendMutation.isPending
                }
              >
                {generateTokenMutation.isPending
                  ? "Generating..."
                  : "Generate Assessment Link"}
              </Button>

              {candidate.email && hasExistingAssessmentData && (
                <Button
                  variant="outline"
                  onClick={() => resumeAndResendMutation.mutate()}
                  disabled={resumeAndResendMutation.isPending}
                >
                  {resumeAndResendMutation.isPending
                    ? "Preparing resume link..."
                    : "Resume Saved Progress & Send Link"}
                </Button>
              )}
            </div>
          )}

          {hasValidToken && (
            <>
              <div className="space-y-2">
                <Label htmlFor="assessment-url">Assessment URL</Label>
                <div className="flex space-x-2">
                  <Input
                    id="assessment-url"
                    value={assessmentUrl}
                    readOnly
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={copyToClipboard}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="text-sm text-gray-600">
                <p>
                  Token expires:{" "}
                  {new Date(candidate.token_expires_at!).toLocaleDateString(
                    "en-GB"
                  )}
                </p>
                <p>Status: {formatCandidateStatus(candidate.status)}</p>
              </div>

              <div className="flex space-x-2">
                <Button
                  onClick={() => sendEmailMutation.mutate()}
                  disabled={
                    sendEmailMutation.isPending ||
                    resumeAndResendMutation.isPending ||
                    !candidate.email
                  }
                  className="flex-1"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  {sendEmailMutation.isPending ? "Sending..." : "Send Email"}
                </Button>

                <Button
                  variant="outline"
                  onClick={() => generateTokenMutation.mutate()}
                  disabled={
                    generateTokenMutation.isPending ||
                    resumeAndResendMutation.isPending
                  }
                >
                  Regenerate
                </Button>
              </div>

              {candidate.email && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => resumeAndResendMutation.mutate()}
                  disabled={resumeAndResendMutation.isPending}
                >
                  {resumeAndResendMutation.isPending
                    ? "Preparing resume link..."
                    : "Resume From Saved Progress & Send New Link"}
                </Button>
              )}

              <p className="text-xs text-gray-500">
                This keeps saved answers and lets the candidate continue from
                where they left off with a fresh timer.
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
